import clsx from 'clsx'
import { useState } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'
import { useBeaconLogClick } from 'libs/athos/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { ResultImage } from 'libs/athos/components/ResultImage'
import { Price } from 'libs/athos/components/Price'
import { formatMoney } from 'helpers/utils'

const UPSELL_IMAGE_WIDTHS = [160, 240, 320, 480]

const getOptionValue = (variant, matcher) => {
  const opts = variant?.options
  if (opts && typeof opts === 'object' && !Array.isArray(opts)) {
    for (const [key, val] of Object.entries(opts)) {
      if (matcher(key)) {
        return val && typeof val === 'object' ? val.value ?? null : val
      }
    }
  }
  return null
}

const getDisplaySize = (variant) => {
  const optionValue =
    getOptionValue(variant, (key) => /size|value/i.test(key)) ??
    variant?.size ??
    variant?.Size ??
    getOptionValue(variant, (key) => !/colou?r/i.test(key))

  const label =
    optionValue ??
    variant?.option2 ??
    variant?.option1 ??
    variant?.title ??
    variant?.name

  return label != null && String(label).trim() !== '' ? String(label).trim() : 'N/A'
}

const isAvailable = (variant) => variant?.available !== false && variant?.available !== 'false'

/**
 * Price display used when a caller supplies `options.priceOverride` (e.g. GWP
 * discounted / free gifts). Values are in cents. Mirrors the sale styling used
 * elsewhere: "Free" for a zero price, brand-sale colour otherwise, with the
 * original price struck through when a discount is applied.
 */
function OverridePrice({ price, compareAtPrice, className }) {
  const isFree = !price || price <= 0
  const showCompare = compareAtPrice != null && price < compareAtPrice
  return (
    <p className={clsx('flex items-center gap-0.25', className)}>
      <span className="paragraph-s font-bold text-brand-sale uppercase">
        {isFree ? 'Free' : formatMoney(price)}
      </span>
      {showCompare && (
        <span className="line-through paragraph-s text-neutral-secondary">{formatMoney(compareAtPrice)}</span>
      )}
    </p>
  )
}

function SizeSelect({ variants, selectedVariant, onSelect, optionName }) {
  return (
    <div className="grow flex relative w-full h-fit bg-button-white-default">
      <select
        className="appearance-none w-full h-[32px] pl-0.625 pr-2 py-0.25 text-primary paragraph-s bg-none !border-none !outline-none !ring-0 !ring-offset-0 transition-all cursor-pointer link-fade"
        value={selectedVariant ? String(selectedVariant.id) : ''}
        onChange={(event) => {
          const { value } = event.currentTarget
          onSelect(variants.find((variant) => String(variant.id) === value) || null)
        }}
        aria-label={`Select ${optionName}`}
      >
        <option value="" disabled>
          {optionName}
        </option>
        {variants.map((variant) => {
          const available = isAvailable(variant)
          return (
            <option key={variant.id} value={variant.id} disabled={!available}>
              {getDisplaySize(variant)}
              {available ? '' : ' — Sold Out'}
            </option>
          )
        })}
      </select>
      <span className="pointer-events-none absolute top-1/2 right-0.625 -translate-y-1/2 flex items-center w-[11px] h-[6px] children:size-full">
        <Icon id="chevron-down" />
      </span>
    </div>
  )
}

export function ResultUpsellCard({ result, className, dataIndex = '0', options = {} }) {
  const atcText = options.atcText || 'Add To Bag'
  const soldOutText = options.soldOutText || 'Sold Out'

  const variants = Array.isArray(result.variants) ? result.variants : []

  const isGiftCard = result.is_giftcard === 'true' || result.template_suffix === 'gift-card'
  const optionName = isGiftCard ? 'Value' : 'Size'

  const productName = result.mfield_utopia_customer_facing_product_name?.trim()
    ? result.mfield_utopia_customer_facing_product_name
    : result.name

  const url = `/products/${result.handle}`
  const productId = result.parentId ?? result.groupId ?? result.uid

  const hasSingleVariant = variants.length <= 1
  const singleVariant = variants[0]
  const anyAvailable = variants.some(isAvailable)

  const [selectedVariant, setSelectedVariant] = useState(null)
  const [loading, setLoading] = useState(false)

  const clickLogger = useBeaconLogClick()

  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(result)
    clickLogger(result)
  }

  const addToCart = async (variant) => {
    if (!variant) return

    setLoading(true)
    try {
      if (options.onAddToCart) {
        await options.onAddToCart(variant)
      } else if (window.Cart) {
        const variantId = typeof variant.id === 'string' ? parseInt(variant.id, 10) : variant.id
        await window.Cart.addLineItems({ id: variantId, quantity: 1 })
      }
    } catch (error) {
      console.error('Error adding upsell item to cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const buttonClass = 'shrink-0 btn btn-s !px-0'

  const renderAtc = () => {
    if (!anyAvailable) {
      return (
        <div className="flex w-full">
          <button className={clsx(buttonClass, 'btn-primary btn-fill w-full')} aria-label={soldOutText} disabled>
            <span>{soldOutText}</span>
          </button>
        </div>
      )
    }

    if (hasSingleVariant) {
      return (
        <div className="flex w-full">
          <button
            type="button"
            className={clsx(buttonClass, 'btn-primary btn-fill w-full', loading && 'disabled')}
            onClick={() => addToCart(singleVariant)}
            disabled={loading}
            aria-label={atcText}
          >
            <span>Add</span>
          </button>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-0.5">
        <SizeSelect
          variants={variants}
          selectedVariant={selectedVariant}
          onSelect={setSelectedVariant}
          optionName={optionName}
        />

        <div className="flex">
          {selectedVariant ? (
            <button
              type="button"
              className={clsx(buttonClass, 'btn-primary btn-fill w-[58px]', loading && 'disabled')}
              onClick={() => addToCart(selectedVariant)}
              disabled={loading}
              aria-label={atcText}
            >
              <span>Add</span>
            </button>
          ) : (
            <button className={clsx(buttonClass, 'btn-primary btn-fill w-[58px] pointer-events-none')} aria-label="Select">
              <span>Select</span>
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx('flex items-stretch bg-brand-tertiary', className)}
      data-dl-track="select_item"
      data-dl-product-id={productId}
      data-dl-index={dataIndex}
    >
      <a href={url} onClick={handleLogClick} className="shrink-0 w-[100px] md:w-[120px] aspect-1/1" aria-label={result.name}>
        <ResultImage result={result} sizes="(min-width: 768px) 178px, 50vw" widths={UPSELL_IMAGE_WIDTHS} />
      </a>

      <div className="grow flex flex-col justify-between gap-y-0.75 min-w-0 h-auto px-0.75 py-0.5">
        <div className="flex flex-col gap-y-0.375 min-w-0">
          <a href={url} className="paragraph-s font-bold link-fade line-clamp-1" aria-label={result.name} dangerouslySetInnerHTML={{ __html: productName }} />

          {options.priceOverride ? (
            <OverridePrice
              price={options.priceOverride.price}
              compareAtPrice={options.priceOverride.compareAtPrice}
              className="flex items-center gap-0.25"
            />
          ) : (
            <Price
              price={String(result.price ?? '')}
              compareAtPrice={result.compare_at_price != null ? String(result.compare_at_price) : undefined}
              className="flex items-center gap-0.25"
            />
          )}
        </div>

        {renderAtc()}
      </div>
    </div>
  )
}
